// Audit Log UI — REPLACES v1 audit tab (Prompt 12b).
// Four sections: Overview KPIs / Filtered Event Table / Critical Events Stream / Examiner Export.
// Reads LIVE admin_audit_log via /api/v2/admin/audit/*.

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Shield, RefreshCw, Search, Filter, Download, X, Loader2, CheckCircle2,
  AlertTriangle, AlertOctagon, FileText, Flag, Clock, Copy, Wifi,
  ChevronLeft, ChevronRight, ChevronDown, Activity, User, MapPin,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2/admin/audit`;

// ── Helpers ──

const fmtRel = (iso) => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const fmtUTC = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + " UTC";
  } catch { return iso; }
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const STATUS_PILL = {
  success:        "bg-emerald-100 text-emerald-700 border-emerald-200",
  failed:         "bg-rose-100 text-rose-700 border-rose-200",
  review_flagged: "bg-amber-100 text-amber-700 border-amber-200",
};

const MODULE_DOT = {
  "Rails Operations":        "bg-teal-500",
  "Treasury & Reserves":     "bg-emerald-500",
  "Cross-Border Operations": "bg-purple-500",
  "Tokenization Console":    "bg-lime-500",
  "v1 Operations":           "bg-slate-400",
};

const StatusPill = ({ status }) => (
  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${STATUS_PILL[status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
    {status?.replace(/_/g, " ")}
  </span>
);

const ModuleLabel = ({ module }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-slate-700">
    <span className={`w-2 h-2 rounded-full ${MODULE_DOT[module] || "bg-slate-300"}`} />
    {module}
  </span>
);

const Toast = ({ msg, kind = "success", onClose }) => {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 2400);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  const bg = kind === "success" ? "bg-slate-900" : "bg-rose-600";
  return (
    <div className={`fixed bottom-6 right-6 ${bg} text-white px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 z-50 text-sm`} data-testid="audit-toast">
      <CheckCircle2 className="w-4 h-4" />{msg}
    </div>
  );
};

// Reusable multi-select dropdown
const MultiSelect = ({ label, options, selected, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const toggle = (opt) => {
    onChange(selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt]);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid={testid}
        className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white hover:bg-slate-50 flex items-center justify-between gap-2">
        <span className="truncate">
          {selected.length === 0 ? <span className="text-slate-400">{label}</span> :
            <span className="text-slate-900">{label}: {selected.length}</span>}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-20" data-testid={`${testid}-dropdown`}>
            {options.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-400">No options</p>
            ) : options.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="rounded" />
                <span className="truncate">{opt}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 1 — Overview
// ─────────────────────────────────────────────────

const OverviewSection = ({ data }) => {
  if (!data) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const maxCount = Math.max(...data.top_admins.map(a => a.event_count), 1);
  return (
    <div className="space-y-4" data-testid="audit-overview-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="audit-kpi-lifetime">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Lifetime</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{data.kpis.total_lifetime.toLocaleString()}</p>
          <p className="text-[11px] text-slate-500">Total audit events</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="audit-kpi-24h">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Last 24h</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{data.kpis.last_24h.toLocaleString()}</p>
          <p className="text-[11px] text-slate-500">Events recorded</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="audit-kpi-7d">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Last 7d</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{data.kpis.last_7d.toLocaleString()}</p>
          <p className="text-[11px] text-slate-500">Events recorded</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="audit-kpi-admins">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Active admins</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{data.kpis.active_admins_30d}</p>
          <p className="text-[11px] text-slate-500">Last 30 days</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="audit-top-admins">
        <h3 className="text-sm font-bold text-slate-900 mb-3">Top Admin Activity — Last 30 Days</h3>
        {data.top_admins.length === 0 ? (
          <p className="text-xs text-slate-400 py-2">No admin activity in the last 30 days.</p>
        ) : (
          <div className="space-y-2">
            {data.top_admins.map((a) => (
              <div key={a.admin_email} className="grid grid-cols-12 gap-3 items-center text-sm" data-testid={`audit-top-admin-${a.admin_email}`}>
                <div className="col-span-5 truncate font-mono text-xs text-slate-700">{a.admin_email}</div>
                <div className="col-span-5 h-5 bg-slate-100 rounded-sm overflow-hidden">
                  <div className="h-full bg-slate-700 rounded-sm" style={{ width: `${(a.event_count / maxCount) * 100}%` }} />
                </div>
                <div className="col-span-2 text-right text-xs font-bold text-slate-900 tabular-nums">{a.event_count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 2 — Event Table + Detail Modal
// ─────────────────────────────────────────────────

const EventDetailModal = ({ eventId, onClose, onFlagged }) => {
  const [data, setData] = useState(null);
  const [flagReason, setFlagReason] = useState("");
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    setData(null);
    axios.get(`${API}/events/${eventId}`).then(r => setData(r.data));
  }, [eventId]);

  if (!eventId) return null;

  const flag = async () => {
    setFlagging(true);
    try {
      await axios.post(`${API}/events/${eventId}/flag`, { reason: flagReason });
      onFlagged?.();
      onClose();
    } catch {
      // noop — toast happens outside
    } finally {
      setFlagging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="audit-event-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {!data ? (
          <div className="h-48 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900 font-mono">{data.event.action}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{fmtUTC(data.event.timestamp)} · <ModuleLabel module={data.event.module} /></p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="audit-event-modal-close">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Admin metadata */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-testid="audit-admin-meta">
                <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Admin User</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] uppercase text-slate-500">Email</p>
                    <p className="font-mono text-slate-900">{data.event.admin_email}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-500">Role</p>
                    <p className="text-slate-900">{data.admin?.role || "admin"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-500">Last Login</p>
                    <p className="text-slate-900">{data.admin?.last_login ? fmtRel(data.admin.last_login) : "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-500">IP (this event)</p>
                    <p className="font-mono text-slate-900 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{data.event.ip_address}</p>
                  </div>
                </div>
                {data.ip_history.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-[10px] uppercase text-slate-500 mb-1.5">Recent IP history</p>
                    <div className="flex flex-wrap gap-1.5">
                      {data.ip_history.map(ip => (
                        <span key={ip.ip} className="text-[11px] font-mono bg-white border border-slate-200 rounded px-2 py-0.5 text-slate-700">
                          {ip.ip}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Payload */}
              <div data-testid="audit-event-payload">
                <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Full Event Payload (JSON)</h4>
                <pre className="bg-slate-900 text-emerald-300 text-xs p-4 rounded-lg overflow-x-auto font-mono leading-relaxed">
{JSON.stringify(data.event, null, 2)}
                </pre>
              </div>

              {/* Related entity */}
              {data.related && (
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-3" data-testid="audit-related-entity">
                  <p className="text-xs font-bold text-sky-800">Related Entity</p>
                  <p className="text-sm text-sky-900 mt-0.5">
                    <span className="font-mono">{data.related.id}</span>
                    {" — "}
                    <span className="text-sky-700">{data.related.link_hint}</span>
                  </p>
                </div>
              )}

              {/* Flag */}
              {data.event.review_flagged ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2" data-testid="audit-already-flagged">
                  <Flag className="w-4 h-4 text-amber-600" />
                  <p className="text-sm text-amber-900">This event has already been flagged for review.</p>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <label className="text-xs font-bold uppercase text-slate-500">Flag for Review</label>
                  <div className="mt-2 flex gap-2">
                    <input type="text" value={flagReason} onChange={e => setFlagReason(e.target.value)}
                      placeholder="Reason (optional)"
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="audit-flag-reason" />
                    <button onClick={flag} disabled={flagging} data-testid="audit-flag-submit"
                      className="px-3 py-2 text-xs font-bold rounded-md bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 flex items-center gap-1.5">
                      {flagging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />} Flag
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EventTableSection = ({ toast }) => {
  const [filters, setFilters] = useState({
    start_date: daysAgoStr(7),
    end_date: todayStr(),
    admin_emails: [],
    actions: [],
    modules: [],
    ip: "",
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        admin_emails: filters.admin_emails.length ? filters.admin_emails.join(",") : undefined,
        actions: filters.actions.length ? filters.actions.join(",") : undefined,
        modules: filters.modules.length ? filters.modules.join(",") : undefined,
        ip: filters.ip || undefined,
        page, per_page: 50,
      };
      const r = await axios.get(`${API}/events`, { params });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setFilters({ start_date: daysAgoStr(7), end_date: todayStr(), admin_emails: [], actions: [], modules: [], ip: "" });
    setPage(1);
  };

  const search = () => { setPage(1); load(); };

  if (!data) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-48 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="audit-events-section">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Event Log</h3>
          <p className="text-xs text-slate-500">{data.total.toLocaleString()} events · page {data.page} of {data.total_pages}</p>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>

      {/* Filter bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4" data-testid="audit-filter-bar">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">From</label>
            <input type="date" value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})}
              className="mt-1 w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm" data-testid="audit-filter-start" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">To</label>
            <input type="date" value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})}
              className="mt-1 w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm" data-testid="audit-filter-end" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Admin</label>
            <MultiSelect label="Any admin" testid="audit-filter-admin"
              options={data.filter_options.admin_emails}
              selected={filters.admin_emails}
              onChange={v => setFilters({...filters, admin_emails: v})} />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Module</label>
            <MultiSelect label="Any module" testid="audit-filter-module"
              options={data.filter_options.modules}
              selected={filters.modules}
              onChange={v => setFilters({...filters, modules: v})} />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Action</label>
            <MultiSelect label="Any action" testid="audit-filter-action"
              options={data.filter_options.actions}
              selected={filters.actions}
              onChange={v => setFilters({...filters, actions: v})} />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">IP Address</label>
            <input type="text" value={filters.ip} onChange={e => setFilters({...filters, ip: e.target.value})}
              placeholder="e.g., 192.168.1.45"
              className="mt-1 w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm font-mono" data-testid="audit-filter-ip" />
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            <button onClick={search} data-testid="audit-filter-search"
              className="px-4 py-1.5 text-xs font-bold rounded-md bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5" /> Search
            </button>
            <button onClick={reset} data-testid="audit-filter-reset"
              className="px-4 py-1.5 text-xs font-semibold rounded-md border border-slate-200 hover:bg-slate-50">
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-events-table">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2.5">Timestamp (UTC)</th>
              <th className="text-left px-3 py-2.5">Admin</th>
              <th className="text-left px-3 py-2.5">Module</th>
              <th className="text-left px-3 py-2.5">Action</th>
              <th className="text-left px-3 py-2.5">Target</th>
              <th className="text-left px-3 py-2.5">IP</th>
              <th className="text-left px-3 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.events.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No events match these filters.</td></tr>
            ) : data.events.map(e => (
              <tr key={e.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(e.id)} data-testid={`audit-event-row-${e.id}`}>
                <td className="px-3 py-2.5 text-xs text-slate-500 font-mono whitespace-nowrap">{fmtUTC(e.timestamp)}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{e.admin_email}</td>
                <td className="px-3 py-2.5"><ModuleLabel module={e.module} /></td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-900">{e.action}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[220px] truncate" title={e.target_entity}>{e.target_entity}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{e.ip_address}</td>
                <td className="px-3 py-2.5"><StatusPill status={e.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between text-xs text-slate-500" data-testid="audit-pagination">
        <span>Showing {((data.page - 1) * data.per_page) + 1}–{Math.min(data.page * data.per_page, data.total)} of {data.total.toLocaleString()}</span>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={data.page <= 1}
            className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-30" data-testid="audit-page-prev">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="px-3 py-1 bg-slate-100 rounded-md font-mono">{data.page} / {data.total_pages}</span>
          <button onClick={() => setPage(p => Math.min(data.total_pages, p + 1))} disabled={data.page >= data.total_pages}
            className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-30" data-testid="audit-page-next">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <EventDetailModal eventId={selected} onClose={() => setSelected(null)}
        onFlagged={() => { toast("Event flagged for review"); load(); }} />
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 3 — Critical Events Stream
// ─────────────────────────────────────────────────

const CriticalSection = ({ onOpenEvent }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/critical`).then(r => setData(r.data));
  }, []);

  // Must run unconditionally (rules of hooks). Safe to derive from null data.
  const combined = useMemo(() => {
    const arr = data ? [...data.audit_events, ...data.compliance_events] : [];
    return arr.sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
  }, [data]);

  if (!data) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="audit-critical-section">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <AlertOctagon className="w-4 h-4 text-amber-600" /> Critical Events
          </h3>
          <p className="text-xs text-slate-500">High-consequence admin actions requiring elevated audit visibility</p>
        </div>
        <span className="text-xs font-bold text-slate-700">{combined.length.toLocaleString()} events</span>
      </div>

      {combined.length === 0 ? (
        <div className="py-10 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-700">No critical events recorded</p>
          <p className="text-xs text-slate-500">Clean operational period.</p>
        </div>
      ) : (
        <div className="relative" data-testid="audit-critical-timeline">
          {/* vertical spine */}
          <div className="absolute left-3 top-1 bottom-1 w-px bg-slate-200" />
          <ul className="space-y-4">
            {combined.slice(0, 50).map(e => {
              const severity = e.severity || (e.action?.includes("emergency") ? "critical" : "high");
              const Icon = severity === "critical" ? AlertOctagon : AlertTriangle;
              const dotColor = severity === "critical" ? "bg-rose-500" : "bg-amber-500";
              const iconColor = severity === "critical" ? "text-rose-600" : "text-amber-600";
              return (
                <li key={e.id} className="relative pl-10" data-testid={`audit-critical-row-${e.id}`}>
                  <span className={`absolute left-2 top-1.5 w-3 h-3 rounded-full ${dotColor} ring-4 ring-white`} />
                  <button onClick={() => e.source !== "compliance_events" && onOpenEvent?.(e.id)}
                    className={`w-full text-left bg-slate-50 border border-slate-200 rounded-lg p-3 ${e.source === "compliance_events" ? "cursor-default" : "hover:bg-slate-100"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />
                        <span className="font-mono text-xs font-bold text-slate-900">{e.action}</span>
                      </div>
                      <span className="text-[10px] uppercase font-bold text-slate-500">{fmtRel(e.timestamp)}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1 ml-6">
                      <span className="font-mono">{e.admin_email}</span>
                      {e.target_entity && e.target_entity !== "—" && <> · target <span className="font-mono">{e.target_entity}</span></>}
                      {e.detail && <> · {e.detail}</>}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 4 — Examiner Export
// ─────────────────────────────────────────────────

const ExportSection = ({ toast }) => {
  const [stdForm, setStdForm] = useState({
    start_date: daysAgoStr(30), end_date: todayStr(),
    modules: [], format: "csv", include: "all",
  });
  const [soForm, setSoForm] = useState({
    start_date: daysAgoStr(30), end_date: todayStr(),
  });
  const [feed, setFeed] = useState(null);
  const [lastSignoff, setLastSignoff] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    axios.get(`${API}/feed/status`).then(r => setFeed(r.data));
    axios.get(`${API}/sign-off/last`).then(r => setLastSignoff(r.data));
  }, []);

  const download = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const runStandard = async () => {
    setExporting("standard");
    try {
      const r = await axios.post(`${API}/export/standard`, stdForm, { responseType: "blob" });
      const ct = r.headers["content-type"] || "application/octet-stream";
      const cd = r.headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const fn = match ? match[1] : `audit-log.${stdForm.format}`;
      download(new Blob([r.data], { type: ct }), fn);
      toast(`Exported ${fn}`);
    } catch (e) {
      toast("Export failed", "error");
    } finally { setExporting(null); }
  };

  const runSignoff = async () => {
    setExporting("signoff");
    try {
      const r = await axios.post(`${API}/export/sign-off`, soForm, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const fn = match ? match[1] : `signoff.pdf`;
      download(new Blob([r.data], { type: "application/pdf" }), fn);
      toast(`Sign-off report exported`);
      const last = await axios.get(`${API}/sign-off/last`);
      setLastSignoff(last.data);
    } catch (e) {
      toast("Export failed", "error");
    } finally { setExporting(null); }
  };

  const copyToken = async () => {
    if (!feed?.bearer_token_preview) return;
    try { await navigator.clipboard.writeText(feed.bearer_token_preview); toast("Token copied"); } catch { toast("Copy failed", "error"); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="audit-export-section">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900">Regulatory & Examiner Export</h3>
        <p className="text-xs text-slate-500">Hand audit data to FFIEC examiners or external auditors</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* A. Standard */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40" data-testid="audit-export-standard">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-slate-700" />
            <h4 className="text-sm font-bold text-slate-900">A. Standard Audit Export</h4>
          </div>
          <div className="space-y-2.5 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={stdForm.start_date} onChange={e => setStdForm({...stdForm, start_date: e.target.value})}
                className="px-2 py-1.5 border border-slate-200 rounded-md" data-testid="audit-std-start" />
              <input type="date" value={stdForm.end_date} onChange={e => setStdForm({...stdForm, end_date: e.target.value})}
                className="px-2 py-1.5 border border-slate-200 rounded-md" data-testid="audit-std-end" />
            </div>
            <MultiSelect label="All modules" testid="audit-std-modules"
              options={["Rails Operations", "Treasury & Reserves", "Cross-Border Operations", "Tokenization Console", "v1 Operations"]}
              selected={stdForm.modules}
              onChange={v => setStdForm({...stdForm, modules: v})} />
            <select value={stdForm.format} onChange={e => setStdForm({...stdForm, format: e.target.value})}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-md bg-white" data-testid="audit-std-format">
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="pdf">PDF Report</option>
            </select>
            <select value={stdForm.include} onChange={e => setStdForm({...stdForm, include: e.target.value})}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-md bg-white" data-testid="audit-std-include">
              <option value="all">All events</option>
              <option value="success">Success only</option>
              <option value="failed">Failed only</option>
              <option value="critical">Critical only</option>
            </select>
          </div>
          <button onClick={runStandard} disabled={exporting === "standard"} data-testid="audit-std-generate"
            className="mt-3 w-full px-3 py-2 text-xs font-bold rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-1.5">
            {exporting === "standard" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Generate Export
          </button>
        </div>

        {/* B. Sign-off */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40" data-testid="audit-export-signoff">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-slate-700" />
            <h4 className="text-sm font-bold text-slate-900">B. Compliance Officer Sign-Off Report</h4>
          </div>
          <p className="text-xs text-slate-500 mb-3">Pre-formatted PDF — executive summary, critical events, admin activity, sanctions + Travel Rule + attestation summary.</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <input type="date" value={soForm.start_date} onChange={e => setSoForm({...soForm, start_date: e.target.value})}
              className="px-2 py-1.5 border border-slate-200 rounded-md" data-testid="audit-so-start" />
            <input type="date" value={soForm.end_date} onChange={e => setSoForm({...soForm, end_date: e.target.value})}
              className="px-2 py-1.5 border border-slate-200 rounded-md" data-testid="audit-so-end" />
          </div>
          <button onClick={runSignoff} disabled={exporting === "signoff"} data-testid="audit-so-generate"
            className="mt-3 w-full px-3 py-2 text-xs font-bold rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-1.5">
            {exporting === "signoff" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
            Generate Sign-Off Report
          </button>
          {lastSignoff?.generated_at && (
            <p className="text-[11px] text-slate-500 mt-2" data-testid="audit-so-last">
              Last generated: {fmtRel(lastSignoff.generated_at)} by <span className="font-mono">{lastSignoff.operator}</span>
            </p>
          )}
        </div>

        {/* C. Feed */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40" data-testid="audit-export-feed">
          <div className="flex items-center gap-2 mb-2">
            <Wifi className="w-4 h-4 text-slate-700" />
            <h4 className="text-sm font-bold text-slate-900">C. Real-Time Audit Feed</h4>
          </div>
          <p className="text-xs text-slate-500 mb-3">Connect your SIEM or external auditor's system to receive real-time audit events.</p>
          {feed && (
            <div className="space-y-2 text-xs">
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-500">Endpoint</p>
                <code className="block bg-white border border-slate-200 rounded-md px-2 py-1 mt-0.5 text-slate-800 break-all">GET {feed.endpoint_url}</code>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-500">Bearer Token</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <code className="flex-1 bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-800 truncate font-mono">{feed.bearer_token_preview}</code>
                  <button onClick={copyToken} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-100" data-testid="audit-feed-copy" title="Copy">
                    <Copy className="w-3 h-3 text-slate-600" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-600">Format: <span className="font-mono text-[11px]">{feed.format}</span></p>
              <div className="flex items-center gap-1.5 pt-1" data-testid="audit-feed-status">
                <span className={`w-2 h-2 rounded-full ${feed.status === "connected" ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`} />
                <span className={`text-[10px] uppercase font-bold ${feed.status === "connected" ? "text-emerald-700" : "text-rose-700"}`}>{feed.status}</span>
                <span className="text-[11px] text-slate-500">· last delivered {fmtRel(feed.last_delivered_at)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// Main tab
// ─────────────────────────────────────────────────

export const AuditLogTab = () => {
  const [overview, setOverview] = useState(null);
  const [toastState, setToastState] = useState({ msg: "", kind: "success" });
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [drillEvent, setDrillEvent] = useState(null);
  const toast = (msg, kind = "success") => setToastState({ msg, kind });

  const loadOverview = useCallback(async () => {
    const r = await axios.get(`${API}/overview`);
    setOverview(r.data);
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview, refreshKey]);

  const onRefresh = async () => {
    setRefreshing(true);
    try { await loadOverview(); setRefreshKey(k => k + 1); } finally { setRefreshing(false); }
  };

  return (
    <div data-testid="admin-audit">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Audit Log</h2>
          <p className="text-sm text-slate-500">Examiner-grade evidence trail · live admin_audit_log + compliance_events · all admin modules</p>
        </div>
        <button onClick={onRefresh} disabled={refreshing} data-testid="audit-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="space-y-6">
        <OverviewSection data={overview} />
        <EventTableSection toast={toast} />
        <CriticalSection onOpenEvent={(id) => setDrillEvent(id)} />
        <ExportSection toast={toast} />
      </div>

      {/* Drill modal opened from critical timeline */}
      <EventDetailModal eventId={drillEvent} onClose={() => setDrillEvent(null)}
        onFlagged={() => toast("Event flagged for review")} />

      <Toast msg={toastState.msg} kind={toastState.kind} onClose={() => setToastState({ msg: "", kind: "success" })} />
    </div>
  );
};
